import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import { readBlockCtaIntent, resolveBlockCtaHref } from '../../lib/resolveSectionHref';
import type { BlockRenderProps } from '../../types';
import { resolveHeroCopySide, resolveHeroDishSide } from '@web-studio/shared-schemas';

export function HeroFullBleedImage({
  block,
  locale,
  defaultLocale = locale,
  isActive,
  activeEditTarget,
  editable,
  headingLevel = 'h1',
  pages,
  siteMode,
  allBlocks,
}: BlockRenderProps) {
  const Heading = headingLevel;
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryIntent = readBlockCtaIntent(block.content, locale, {
    hrefFieldKey: 'ctaPrimaryHref',
    fallback: 'book',
  });
  const ctaPrimaryHref = resolveBlockCtaHref(ctaPrimaryIntent, {
    locale,
    defaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaPrimaryHref',
    siteMode,
    pages,
    allBlocks,
  });
  const heroImage = pickMediaUrl(block, 'heroImage');
  const heroImageSrc = heroImage ? buildSiteImageProxyUrl(heroImage, 1800, 78) : undefined;
  const heroImageSrcSet = heroImage
    ? buildSiteImageSrcSet(heroImage, [480, 768, 1200, 1600, 2000], 78)
    : undefined;
  const imageAlt = heading || eyebrow || 'Hero';
  const target = isActive ? activeEditTarget : null;
  const dishSide = resolveHeroDishSide(block.content);
  const copySide = resolveHeroCopySide(dishSide);

  return (
    <section
      className={['ws-hero-fullbleed', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      data-dish-side={dishSide}
      data-copy-side={copySide}
      id="top"
    >
      <div className="ws-hero-fullbleed__stage" aria-hidden={!heroImage}>
        {heroImage ? (
          <EditableMedia
            fieldKey="heroImage"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-fullbleed__photo"
            src={heroImageSrc}
            srcSet={heroImageSrcSet}
            sizes={SITE_IMAGE_SIZES.heroFullBleed}
            alt={imageAlt}
            width={1600}
            height={900}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        ) : (
          <div className="ws-hero-fullbleed__placeholder" aria-hidden />
        )}
        {/* Top band for nav; side veil softens copy side only */}
        <div className="ws-hero-fullbleed__veil" aria-hidden />
        <div className="ws-hero-fullbleed__side-veil" aria-hidden />
      </div>
      <div id="about" className="ws-hero-fullbleed__copy">
        <div className="ws-hero-fullbleed__pocket">
          <EditableContent
            fieldKey="eyebrow"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-fullbleed__eyebrow"
          >
            {eyebrow}
          </EditableContent>
          <EditableContent
            as={Heading}
            fieldKey="heading"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-fullbleed__title"
          >
            {heading}
          </EditableContent>
          <EditableContent
            fieldKey="subheading"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-fullbleed__lead"
          >
            {subheading}
          </EditableContent>
          {ctaPrimary ? (
            <EditableContent
              as="a"
              fieldKey="ctaPrimary"
              editable={editable}
              activeEditTarget={target}
              className="ws-hero-fullbleed__cta"
              href={ctaPrimaryHref}
            >
              {ctaPrimary}
            </EditableContent>
          ) : null}
        </div>
      </div>
    </section>
  );
}
